import { useEffect, useRef, useState } from "preact/hooks";
import { Link } from "wouter-preact";
import { SUPPORT_URL } from "../lib/config";
import { me, session, signOut } from "../state";

/**
 * Drawer footer: who is signed in and how many questions are left today, with a
 * small menu (profile / support / sign out) on click. Always visible at the
 * bottom of the sidebar, outside the scrolling history.
 */
export function UserMenu({ onNavigate }: { onNavigate?: () => void } = {}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close the menu on Escape and on clicks outside it.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    const onDown = (event: MouseEvent) => {
      const root = rootRef.current;
      if (
        root && event.target instanceof Node && !root.contains(event.target)
      ) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  const profile = me.value;
  const email = session.value?.email ?? "";
  const name = profile?.name ?? email;
  const questionsLeft = profile?.questionsLeft;
  const initial = (name || "?").trim().charAt(0).toUpperCase();

  return (
    <div class="user" ref={rootRef}>
      {open && (
        <div class="user__menu" role="menu">
          <Link
            class="user__menu-item"
            role="menuitem"
            href="/profile"
            onClick={() => {
              setOpen(false);
              onNavigate?.();
            }}
          >
            Profile
          </Link>
          <a
            class="user__menu-item"
            role="menuitem"
            href={SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Support This Project
          </a>
          <button
            type="button"
            class="user__menu-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}

      <button
        type="button"
        class="user__button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {profile?.avatarUrl
          ? <img class="user__avatar" src={profile.avatarUrl} alt="" />
          : (
            <span class="user__avatar user__avatar--initial" aria-hidden="true">
              {initial}
            </span>
          )}
        <span class="user__meta">
          <span class="user__name">{name}</span>
          {questionsLeft != null && (
            <span class="user__allowance">
              {questionsLeft > 0
                ? `${questionsLeft} ${
                  questionsLeft === 1 ? "question" : "questions"
                } left today`
                : "Out of questions today"}
            </span>
          )}
        </span>
      </button>
    </div>
  );
}
